// The club page (X45, Grant 2026-10-19): "Requiring your attention", "Memberships & passes" and "Your bookings"
// are no longer full sections. Each is one entry in a row under the header (icon, label, a count) that opens a
// dialog, driven by ?show=attention|memberships|bookings so a refresh or a shared link lands on the same dialog.
// Competitions lead the left column, grouped by format; Club stats lead the right.
import {BOOKING_ICON} from '~/utils/bookingsMember';
import {FORMAT_KEYS, formatExplainers} from '~/utils/formatExplainers';
import {displayAsNoun, membershipRenewsLine} from '~/utils/memberships';
import {withNoun} from '~/utils/membershipWords';

/** The dialogs the page can show, in the order their entries sit in the row. */
export const CLUB_SHOW = ['attention', 'memberships', 'bookings'];
export const CLUB_SHOW_KEY = 'show';
export const MEMBERSHIPS_ICON = 'mdi-card-account-details-outline';
export const ATTENTION_ICON = 'mdi-account-clock-outline';
/** The signed-out memberships entry: the page behind it needs a sign-in, so the entry says so rather than surprising. */
export const SIGN_IN_MEMBERSHIPS_TITLE = 'Sign in to see memberships & passes';
/** Where a member manages their own memberships & passes (Profile, then Memberships & passes). */
export const MY_MEMBERSHIPS_TO = Object.freeze({path: '/your-profile', query: {tab: 'memberships'}});
/** The held entry's secondary link: the list of what else is on offer (the ?show=memberships dialog). */
export const GET_ANOTHER_LABEL = 'Get another';
/**
 * Grant 2026-11-02 ("every tap goes directly to the specific thing"): the viewer's own membership, opened on the
 * profile's Memberships & passes tab (ProfileMembershipsPanel reads ?membership=), never the tab's list to search.
 */
export const myMembershipTo = (membershipId) => Object.freeze({path: '/your-profile', query: {tab: 'memberships', membership: String(membershipId)}});
/** One type's page under the club's memberships path. */
export const membershipTypeTo = (membershipsPath, type) => membershipsPath ? `${membershipsPath}/${type.id}` : undefined;
/**
 * Grant 2026-11-02 ("If there is only one membership available, what is the point of the dialog?"): the entry when
 * the club offers exactly one type: "Get the Senior membership" / "Get the Summer pass", opening that type's page.
 */
export function singleTypeTitle(type) {
    return `Get the ${withNoun(type?.name, type?.displayAs)}`;
}
/** The single type's second line: its price, and for a pass its length when the price doesn't already say it. */
export function singleTypeSub(type) {
    const price = String(type?.priceSummary ?? '').trim();
    const length = String(type?.lengthSummary ?? '').trim();
    const pass = displayAsNoun(type?.displayAs) === 'pass';
    return [price, pass && length && !price.toLowerCase().includes(length.toLowerCase()) ? length : null].filter(Boolean).join(' · ');
}

// The visitor preview (?viewAs=visitor, the dashboard's "Public page" tile): the page is asked for with
// X-View-As: visitor and every viewer-dependent piece (page/club, the membership types, the players access, the
// member's bookings, the availability card, the recent results, the join settings) belongs to that viewer. The
// page is KEYED on it (clubPageKey, definePageMeta's key) so entering and leaving the preview remount the page and
// ask for everything again, exactly as a fresh load does. Before this "Back to my view" only dropped the query:
// page/club was re-asked by a watch but the mount-time loads were not, so the admin came back to a page with no
// Memberships & passes entry, no bookings and whatever else the preview had left (Grant 2026-10-20, X70).
export const VIEW_AS_KEY = 'viewAs';

/** Whether the route's query asks for the visitor preview (only the value "visitor" counts; an array keeps its first). */
export function isVisitorPreview(query) {
    const raw = query?.[VIEW_AS_KEY];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v === 'visitor';
}

/**
 * The page component's key: the path (the default key) plus the preview when it is on. ?show= and every other
 * query key stay out of it, so opening a dialog never remounts the page.
 */
export function clubPageKey(route) {
    const path = route?.path ?? '';
    return isVisitorPreview(route?.query) ? `${path}?${VIEW_AS_KEY}=visitor` : path;
}

/** Which dialog ?show= asks for, or null (unknown values are ignored; an array keeps its first value). */
export function clubShowFromQuery(query) {
    const raw = query?.[CLUB_SHOW_KEY];
    const v = Array.isArray(raw) ? raw[0] : raw;
    if (v === undefined || v === null) return null;
    const s = String(v).toLowerCase();
    return CLUB_SHOW.includes(s) ? s : null;
}

/** The route query with ?show set to `show`, or without it when `show` is null. Every other key is kept. */
export function clubShowQuery(query, show) {
    const next = {...(query ?? {})};
    delete next[CLUB_SHOW_KEY];
    if (show && CLUB_SHOW.includes(show)) next[CLUB_SHOW_KEY] = show;
    return next;
}

/** "5 need your attention" / "1 needs your attention". */
export function attentionTitle(n) {
    return `${n} ${n === 1 ? 'needs' : 'need'} your attention`;
}

/**
 * "Requests to join this club or one of its competitions": what the attention list holds. The API lists this club's
 * requests only (X48, Grant 2026-10-19): to join the club, or a competition, meet, ladder, cycle or tournament of it,
 * never another club the viewer also administers (the player page keeps every club's).
 */
export function attentionSub(n) {
    return n === 1
        ? 'A request to join this club or one of its competitions'
        : 'Requests to join this club or one of its competitions';
}

/** "Senior, Junior, Social and 3 more" from the live types, for the memberships entry's one line. */
export function namesLine(names, max = 3) {
    const list = (names ?? []).filter(Boolean);
    if (!list.length) return '';
    if (list.length <= max) return list.join(', ');
    const rest = list.length - max;
    return `${list.slice(0, max).join(', ')} and ${rest} more`;
}

/**
 * "Your membership" / "Your pass" / "Your passes" / "Your memberships & passes": the held entry's title, from what
 * the viewer holds here (Grant 2026-11-02).
 */
export function heldTitle(held) {
    const nouns = [...new Set((held ?? []).map(m => displayAsNoun(m.displayAs)))];
    if (nouns.length > 1) return 'Your memberships & passes';
    const noun = nouns[0] ?? 'membership';
    return `Your ${(held ?? []).length > 1 ? `${noun}${noun.endsWith('s') ? 'es' : 's'}` : noun}`;
}

/** "Senior · Renews 18 October 2027" for one held membership, the names for several (the renewal line is theirs to read on the profile). */
export function heldSub(held) {
    const list = held ?? [];
    if (list.length === 1) return [list[0].membershipTypeName, membershipRenewsLine(list[0])].filter(Boolean).join(' · ');
    return namesLine(list.map(m => m.membershipTypeName));
}

/**
 * The entries in the row under the header, in the order they always keep (attention, memberships, bookings), each
 * only when it applies to this viewer:
 * - attention: an admin with applications waiting (warning tint; the count is the title);
 * - memberships: the club has memberships on and live types were loaded, or the viewer is signed out (the list
 *   needs a sign-in, so that entry says so, "Sign in to see memberships & passes", and links to the memberships
 *   page, whose auth middleware sends them to sign in and back there: R22 Low 2). The visitor preview passes
 *   signedIn false and gets the visitor's entry, as a visitor does (X73). Holding one here (Grant 2026-11-02: "it
 *   still shows at the top and looks like I need to press it"), the entry is a status instead, "Your membership ·
 *   Senior · Renews ...", success-toned, linking to the viewer's own under Profile, with "Get another" beside it
 *   opening the same list (`more`), where the held types are tagged Yours; each held one is its own entry (the
 *   first keyed `memberships`), opening THAT membership on the profile (Grant 2026-11-02: never a list to search);
 *   with exactly one type on offer the entry opens that type's page (held: the membership) and there is no list and
 *   no "Get another" (Grant 2026-11-02: "what is the point of the dialog?");
 * - bookings: a signed-in player or admin with a booking coming up at a club that takes bookings.
 * `preview` keeps the admin's own entries (attention, bookings) out whatever else is passed.
 */
export function clubEntries({
    admin = false, applications = 0, membershipsOn = false, signedIn = false, preview = false,
    membershipTypes = [], membershipsBlurb = '', membershipsPath = '', bookingsOn = false, bookings = [], membershipsLoading = false,
    hasLiveTypes = null, held = [],
} = {}) {
    const out = [];
    if (admin && !preview && applications > 0) {
        out.push({
            key: 'attention', icon: ATTENTION_ICON, tone: 'warn',
            title: attentionTitle(applications), sub: attentionSub(applications), count: null,
        });
    }
    // X221 (R90 N-1): page/club says whether the club has a live, public type (hasLiveMembershipTypes). false → no
    // entry at all, signed in or out (nothing to sign in for), so there is never a placeholder that goes away;
    // null (an older API) keeps the X219 behaviour.
    if (membershipsOn && hasLiveTypes !== false) {
        if (signedIn && !preview && membershipsLoading) {
            // X219 (R89 P-1): the types load after hydration; hold the entry's place (same size) so nothing below it
            // jumps when it arrives. Never when memberships are off, or (X221) when the club has no live type.
            out.push({key: 'memberships', icon: MEMBERSHIPS_ICON, tone: 'brand', title: 'Memberships & passes', sub: '', count: null, skeleton: true});
        } else if (signedIn && !preview && held.length > 0) {
            // Grant 2026-11-02: theirs already, so a status (no count, no call to action), each opening its own; the
            // list of the others stays one tap away as the last entry's secondary link, unless that one type is all
            // the club offers.
            const single = membershipTypes.length === 1;
            held.forEach((m, i) => out.push({
                key: i === 0 ? 'memberships' : `memberships-${m.id ?? i}`, icon: MEMBERSHIPS_ICON, tone: 'ok',
                title: heldTitle([m]), sub: heldSub([m]), count: null, to: m.id ? myMembershipTo(m.id) : MY_MEMBERSHIPS_TO,
                ...(i === held.length - 1 && !single ? {more: {label: GET_ANOTHER_LABEL, show: 'memberships'}} : {}),
            }));
        } else if (signedIn && !preview && membershipTypes.length === 1) {
            // Grant 2026-11-02: one type on offer, so the entry is that type and opens its page: no dialog for one item.
            const [t] = membershipTypes;
            out.push({
                key: 'memberships', icon: MEMBERSHIPS_ICON, tone: 'brand',
                title: singleTypeTitle(t), sub: singleTypeSub(t), count: null, to: membershipTypeTo(membershipsPath, t),
            });
        } else if (signedIn && !preview && membershipTypes.length > 0) {
            out.push({
                key: 'memberships', icon: MEMBERSHIPS_ICON, tone: 'brand',
                title: 'Memberships & passes', count: membershipTypes.length, sub: namesLine(membershipTypes.map(t => t.name)),
            });
        } else if (!signedIn) {
            out.push({
                key: 'memberships', icon: MEMBERSHIPS_ICON, tone: 'brand',
                title: SIGN_IN_MEMBERSHIPS_TITLE, sub: membershipsBlurb, count: null, to: membershipsPath,
            });
        }
    }
    if (signedIn && !preview && bookingsOn && bookings.length > 0) {
        out.push({
            key: 'bookings', icon: BOOKING_ICON, tone: 'brand',
            title: 'Your bookings', sub: bookings[0]?.line ? `Next: ${bookings[0].line}` : '', count: bookings.length,
        });
    }
    return out;
}

/** The dialog a direct ?show= may open for this viewer: only one whose entry is in the row (or whose entry's `more` link opens it). */
export function clubShowAllowed(show, entries) {
    return !!show && (entries ?? []).some(e => !e.skeleton && ((e.key === show && !e.to) || e.more?.show === show));
}

/**
 * Whether the open dialog's entry has just gone (the last application reviewed, the last booking cancelled): close it
 * and drop ?show=. Only when the entry WAS in the row: the entries load one after another after hydration (types,
 * then bookings), and a ?show=bookings link must survive the row growing before its entry has arrived.
 */
export function shownEntryGone(show, before, after) {
    return !!show && clubShowAllowed(show, before) && !clubShowAllowed(show, after);
}

const COMPETITION_LISTS = [
    {key: 'league', prop: 'league', list: 'leagues'},
    {key: 'americano', prop: 'americano', list: 'americanos'},
    {key: 'ladder', prop: 'ladder', list: 'ladders'},
    {key: 'tournament', prop: 'tournament', list: 'tournaments'},
    {key: 'meet', prop: 'meet', list: 'meets'},
];

/**
 * The club's competitions grouped by format in the formats' own order (box leagues, americano leagues, ladders,
 * tournaments, meets), each group named and iconed from formatExplainers; empty groups are left out. Nothing is
 * dropped: every competition the page carries is in exactly one group.
 */
export function competitionGroups(page) {
    return FORMAT_KEYS.map((key) => {
        const def = COMPETITION_LISTS.find(c => c.key === key);
        const items = page?.[def.list] ?? [];
        const f = formatExplainers[key];
        return {key, prop: def.prop, name: f?.name ?? key, icon: f?.icon ?? null, items};
    }).filter(g => g.items.length > 0);
}

/** How many competitions the page carries (every format). */
export function competitionCount(page) {
    return COMPETITION_LISTS.reduce((n, c) => n + (page?.[c.list]?.length ?? 0), 0);
}

// ── The players list (Grant 2026-10-20): the page shows the FIRST 50 players, "Show all 801 players" reveals the
// rest in the browser (page/club already carries everyone, nothing is fetched again), and the search box filters
// across ALL players, never only the 50 shown. One signed-in server render of an 801-player club held ~440 MB
// of heap for 801 PlayerCards (MEMORY_M4); 50 is ~25 MB. The order is the API's; slicing keeps it.
export const CLUB_PLAYERS_FIRST = 50;

/** The players whose name holds the query, in their given order; an empty (or cleared: null) query is everyone. */
export function searchPlayers(players, query) {
    const q = String(query ?? '').trim().toLowerCase();
    const all = players ?? [];
    if (!q) return all;
    return all.filter(p => (p?.fullName || '').toLowerCase().includes(q));
}

/**
 * What the list renders: the matches of `query` over every player, cut to the first `first` until `showAll`.
 * `matches` is the full match list (its length is the "No players match" / "Show all N" number), `shown` the
 * rows to render, `hidden` how many the cut left out and `capped` whether the Show all control is due.
 */
export function playersToShow(players, {query = '', showAll = false, first = CLUB_PLAYERS_FIRST} = {}) {
    const matches = searchPlayers(players, query);
    const capped = !showAll && matches.length > first;
    const shown = capped ? matches.slice(0, first) : matches;
    return {matches, shown, hidden: matches.length - shown.length, capped};
}

/** "Show all 801 players", or "Show all 64 matches" while a search is on. */
export function showAllPlayersLabel(total, searching = false) {
    return `Show all ${total} ${searching ? (total === 1 ? 'match' : 'matches') : (total === 1 ? 'player' : 'players')}`;
}

/** "Showing the first 50 of 801" under the cut list. */
export function showingFirstLine(shown, total) {
    return `Showing the first ${shown} of ${total}`;
}
